/**
 * A stable per-browser identifier.
 *
 * Likes are keyed on a hash of the guest link plus this value, so that one
 * guest tapping a heart ten times leaves one like — and so that two guests on
 * two phones are two likes. It is a random value in localStorage and nothing
 * more: it is not a device fingerprint, it is not derived from anything about
 * the hardware, and clearing site data resets it, which is acceptable because
 * the worst outcome is a guest's like reappearing after they clear storage.
 *
 * It is sent to the Worker as `likerKey` for uploads, likes, comments and
 * guestbook notes. Likes and comments hash it into salted guest keys. An upload
 * hashes it into a short-lived per-device rate-limit key, and the completed
 * media row stores a salted uploader key instead. It is not a device
 * fingerprint and is not derived from anything about the hardware; clearing
 * site data resets it.
 */

const STORAGE_KEY = "eg_device";

let cached: string | null = null;

export function deviceKey(): string {
  if (cached) return cached;

  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing && /^[0-9a-f]{16,}$/.test(existing)) {
      cached = existing;
      return cached;
    }

    const bytes = crypto.getRandomValues(new Uint8Array(8));
    const value = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    window.localStorage.setItem(STORAGE_KEY, value);
    cached = value;
    return value;
  } catch {
    // Private browsing, a disabled storage partition, or a quota. Fall back to
    // a value that lasts for the life of the page, which degrades to "one like
    // per visit" rather than failing.
    cached ??= Math.random().toString(36).slice(2, 18);
    return cached;
  }
}

/** Copy text, with a fallback for browsers that refuse the async clipboard. */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
